import React from 'react';
import Drawer from '@app/component/overlays/drawer/Drawer';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';
import { useQueries, useQuery } from '@tanstack/react-query';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import Utilities from '@util/Utilities';
import PriceSubmissionLogTableFragment from './fragment/PriceSubmissionLogTableFragment';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import _isEmpty from 'lodash/isEmpty';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import MarketStateService from '@app/model/market_state/service/MarketStateService';

type PriceSubmissionLogForLogIdDrawerProps = {
	submissionId: number;
	onClose: () => void;
};

const PriceSubmissionDetailsDrawer = React.memo(function PriceSubmissionLogForLogIdDrawer({
	submissionId,
	onClose,
}: PriceSubmissionLogForLogIdDrawerProps) {
	const { companyHotel } = useSelectedCompanyHotel();
	const drawer = useDrawer();
	const [selectedStatDate, setSelectedStatDate] = React.useState<string | undefined>(undefined);

	const results = useQueries({
		queries: [
			{
				queryKey: PricingAutomationQueryKeys.pricingRooms(companyHotel.id),
				queryFn: () => PricingAutomationService.findCompanyHotelPricingRooms(companyHotel.id),
				enabled: companyHotel?.id !== undefined && companyHotel.id !== null,
			},
			{
				queryKey: PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id),
				queryFn: () =>
					PricingAutomationService.findCompanyHotelPricingArchitectures(companyHotel.id),
			},
			{
				queryKey: PricingAutomationQueryKeys.pricingSubmission(companyHotel.id, submissionId),
				queryFn: () =>
					PricingAutomationService.findCompanyHotelPricingSubmission(companyHotel.id, submissionId),
			},
		],
	});
	const submission = results[2].data;
	const itemsByStatDate = React.useMemo(() => {
		if (submission === undefined || submission === null) {
			return {};
		}

		return submission.items.reduce<Record<string, typeof submission.items>>((grouped, item) => {
			grouped[item.statDate] = grouped[item.statDate] ?? [];
			grouped[item.statDate].push(item);
			return grouped;
		}, {});
	}, [submission]);
	const statDates = Object.keys(itemsByStatDate).sort();
	const activeStatDate =
		selectedStatDate !== undefined && statDates.includes(selectedStatDate)
			? selectedStatDate
			: statDates[0];
	const recommendationStatesQuery = useQuery({
		queryKey: MarketStateGraphQLQueryKeys.priceRecommendationStateForStatDate(
			companyHotel.id,
			activeStatDate ?? ''
		),
		queryFn: () =>
			MarketStateService.findLatestPriceRecommendationStateForStatDate(
				companyHotel.id,
				activeStatDate ?? ''
			),
		enabled:
			companyHotel?.id !== undefined && companyHotel.id !== null && activeStatDate !== undefined,
	});

	if (results.some(result => result.isLoading)) {
		return (
			<Drawer
				title={`Price Submission Log`}
				ref={drawer.ref}
				isOpened={true}
				size="large"
				onClose={onClose}
			>
				<div className="mt-6 px-4 sm:px-6">
					<TablePlaceholder />
				</div>
			</Drawer>
		);
	}

	if (results.some(result => result.isError)) {
		return (
			<Drawer
				title={`Price Submission Log`}
				ref={drawer.ref}
				isOpened={true}
				size="large"
				onClose={onClose}
			>
				<div className="mt-6 px-4 sm:px-6">
					<Message state="error">Error loading price submission log.</Message>
				</div>
			</Drawer>
		);
	}

	if (
		results.every(result => result.isSuccess && result.data !== undefined && result.data !== null)
	) {
		const pricingRooms = results[0].data ?? [];
		const architectures = results[1].data ?? [];
		return (
			<Drawer
				title={`Price Submission Log`}
				ref={drawer.ref}
				isOpened={true}
				size="large"
				onClose={onClose}
			>
				<div className="mt-6">
					<div className="px-4 sm:px-6 mt-5">
						<div className="space-y-4">
							{submission && (
								<p className="text-sm text-gray-700">
									<span className="font-medium">Submitted by:</span> {submission.getSubmitterName()}
								</p>
							)}
							{_isEmpty(statDates) && (
								<Message state="info">No price submission items found.</Message>
							)}
							{!_isEmpty(statDates) && activeStatDate !== undefined && (
								<>
									<div className="flex items-center gap-x-2 sm:max-w-sm">
										<label
											className="whitespace-nowrap text-sm font-medium text-gray-700"
											htmlFor="price-submission-stat-date"
										>
											Stay Date
										</label>
										<select
											id="price-submission-stat-date"
											aria-label="Select stat date"
											className="w-full input"
											value={activeStatDate}
											onChange={e => setSelectedStatDate(e.target.value)}
										>
											{statDates.map(statDate => (
												<option key={statDate} value={statDate}>
													{Utilities.formatDateString(statDate, 'DD/MM/yyyy', false)}
												</option>
											))}
										</select>
									</div>
									{recommendationStatesQuery.isLoading && <TablePlaceholder />}
									{recommendationStatesQuery.isError && (
										<Message state="error">Error loading price recommendation state.</Message>
									)}
									{recommendationStatesQuery.isSuccess && (
										<PriceSubmissionLogTableFragment
											companyHotel={companyHotel}
											pricingRooms={pricingRooms}
											architectures={architectures}
											statDate={activeStatDate}
											items={itemsByStatDate[activeStatDate]}
											recommendationStates={recommendationStatesQuery.data ?? []}
										/>
									)}
								</>
							)}
						</div>
					</div>
				</div>
			</Drawer>
		);
	}

	return null;
});

export default PriceSubmissionDetailsDrawer;
